import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Divider, Button, Typography, Tooltip, InputBase } from '@mui/material';
import { makeStyles, useTheme } from '@mui/styles';

import { Add, Search, Sort, UploadFile } from '@mui/icons-material';

import { getFBA, selectFBA } from '../reducers/fbaSlice';
import { FBAList } from '../components/fba/FBAList';
import { selectUser } from '../reducers/userSlice';
import { useEffect } from 'react';
import { EditFBA } from '../components/fba/EditFBA';
import { ImportFBA } from '../components/fba/ImportFBA';
import { selectProjects } from '../reducers/projectSlice';


const useStyles = makeStyles((theme) => ({
  root: {
    // backgroundColor: theme.palette.grey[100],
    flexBasis: 320,
    flexShrink: 0,
    flexGrow: 0,
    height: 'calc(100% - 20px)',
    marginTop: 20,
    padding: 10,
    overflowY: 'auto',
    overflowX: 'hidden',
    // borderRight: '1px solid lightgrey',
  },
  fbaButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 10,
    marginBottom: 10
  },
  button: {
    borderRadius: 20
  },
  search: {
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    marginBottom: 5,
    paddingLeft: 5,
    border: `1px solid ${theme.palette.primary.main}`,
    height: 36,
  },
  searchIcon: {
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.primary.main
  },
  inputRoot: {
    color: 'inherit',
  },
  inputInput: {
    // vertical padding + font size from searchIcon
    paddingLeft: 24,
  },
  fba: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  emptyFBA: {
    width: '100%',
    height: '80%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center'
  }
}));

export const FBAListContainer = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();


  const [editFBAVisible, setEditFBAVisible] = useState(false);
  const [importFBAVisible, setImportFBAVisible] = useState(false);

  const [sortDesc, setSortDesc] = useState(true);
  const [expandAll, setExpandAll] = useState(true);
  const [searchFBA, setSearchFBA] = useState("");

  const user = useSelector(selectUser);
  const projects = useSelector(selectProjects);
  const currentProject = projects?.byId?.[projects?.currentProjectId];
  const fba = useSelector(selectFBA);

  const darkMode = user?.value?.darkMode;

  let fbaIds = fba?.allIds?.length > 0 ? fba?.allIds : [];
  fbaIds = sortDesc ? Array.from(fba?.allIds).reverse() : fbaIds;
  fbaIds = fbaIds?.filter(id => fba?.byId?.[id]?.projectId === currentProject?.id || !fba?.byId?.[id]?.projectId); // filter by current project
  if (searchFBA?.length > 0) {
    fbaIds = fbaIds.filter(id => fba.byId[id]?.name?.toLowerCase()?.includes(searchFBA?.toLowerCase()));
  };

  useEffect(() => {
    if ((!fba || fba?.allIds?.length === 0) && user?.value?.email) getUserFBA(user?.value?.email);

    function getUserFBA(email) {
      try {
        dispatch(getFBA(email)).then(res => {
          // add success snackbar
        });
      } catch (error) {
        console.log(error);
        return;
      };
    };
  }, [dispatch, fba, user?.value?.email]);


  function handleSort() {
    setSortDesc(!sortDesc);
  };

  return (
    <Container className={classes.root} style={{
      backgroundColor: theme?.palette?.paper?.[darkMode ? 'dark' : 'light'],
      borderRight: `1px solid ${darkMode ? 'dimgrey' : 'lightgrey'}`
    }}>
      <div style={{display: 'flex', flexDirection: 'column'}}>
        <Typography variant="h6" color="textSecondary" style={{fontSize: 16}}>
          FBA
        </Typography>
        <Typography variant="h5" className={classes.projectName} noWrap>
          {currentProject?.name || ""}
        </Typography>
      </div>

      <div className={classes.fbaButtons}>
        <div>
          <Tooltip title="New FBA analysis">
            <Button startIcon={<Add /> } variant="contained" onClick={() => setEditFBAVisible(true)} color={darkMode ? "inherit" : "primary"} style={{color: darkMode ? '#000' : '#fff'}} size="small">
              New
            </Button>
          </Tooltip>
          <Tooltip title="Import FBA analysis">
            <Button 
              startIcon={<UploadFile /> } 
              variant="outlined" 
              onClick={() => setImportFBAVisible(true)} 
              color={darkMode ? "inherit" : "primary"} 
              style={{color: (darkMode ? '#fff' : theme.palette.primary.main), marginLeft: 10, borderColor: (darkMode ? '#fff' : theme.palette.primary.main)}} 
              size="small"
            >
              Import
            </Button>
          </Tooltip>
        </div>
        <div>
          <Tooltip title="Sort FBA by Date">
            <Button startIcon={<Sort /> } onClick={handleSort} color={darkMode ? "inherit" : "primary"} size="small">
              Sort
            </Button>
          </Tooltip>
        </div>
      </div>

      <div className={classes.search} style={{border: `1px solid ${darkMode ? "lightgrey" : theme.palette.primary.main}`}}>
        <div className={classes.searchIcon}>
          <Search color={darkMode ? "inherit" : "primary"} />
        </div>
        <InputBase
          placeholder="Search FBAs by name"
          classes={{
            root: classes.inputRoot,
            input: classes.inputInput,
          }}
          value={searchFBA}
          onChange={e => setSearchFBA(e.target.value)}
        />
      </div>
      
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <Typography fontSize={'12px'} style={{marginBottom: 5}}>{`Analyses: ${fbaIds?.length || 0} | Sorted by: ${sortDesc ? 'newest' : 'oldest'}`}</Typography>
        <Button size="small" onClick={() => setExpandAll(prev => !prev)}><Typography fontSize={10}>{expandAll ? 'Collapse' : 'Expand'} All</Typography></Button>
      </div>

      <Divider />

      {fba?.allIds?.length > 0 ?
        <div className={classes.fba}>  
          {fbaIds && fbaIds?.length > 0 && fbaIds.map(fbaId => {
            return (
              <FBAList
                fbaId={fbaId}
                key={fbaId}
                expandAll={expandAll}
              />
            );
          })}
      </div>
      :
      <></>
      }

      {editFBAVisible && <EditFBA fbaId={undefined} setEditFBAVisible={setEditFBAVisible} email={user?.value?.email} darkMode={darkMode} />}
      {importFBAVisible && <ImportFBA setImportFBAVisible={setImportFBAVisible} darkMode={darkMode} />}

    </Container>
  );
};
